import { SampleExamModal } from "@/components/SampleExamDialog";
import { ReactNode, Suspense, useCallback, useEffect, useRef, useState } from "react";

import { Outlet, useNavigate, useLocation } from "react-router-dom";
import UserSidebar from "@/components/UserSidebar";
import AdminSidebar from "@/components/AdminSidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, Settings, Shield, Menu } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/contexts/AuthContext";
import { useSafeAreaTop } from "@/hooks/useSafeAreaTop";
import { usePresenceTracker } from "@/hooks/useOnlinePresence";
import { BetaBadge } from "@/components/shared/BetaBadge";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PastDueBanner } from "@/components/PastDueBanner";
import DemoModeIndicator from "@/components/DemoModeIndicator";
import ScreenshotLinkMasker from "@/components/ScreenshotLinkMasker";
import { useScreenshotMode } from "@/contexts/ScreenshotModeContext";

const PageContentSkeleton = () => (
  <div className="space-y-8 animate-pulse" aria-hidden="true">
    <div className="space-y-2">
      <div className="h-7 w-48 rounded bg-muted-foreground/15" />
      <div className="h-4 w-72 rounded bg-muted-foreground/10" />
    </div>
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-32 rounded-lg border bg-card" />
      ))}
    </div>
    <div className="h-48 rounded-lg border bg-card" />
  </div>
);

const SITE_SUFFIX = "A Software Tester";

function titleForPath(pathname: string): string {
  const p = pathname.replace(/\/+$/, "") || "/";
  const map: Array<[RegExp, string]> = [
    [/^\/dashboard/, "Dashboard"],
    [/^\/chapters/, "Chapters"],
    [/^\/practice-history/, "Practice History"],
    [/^\/practice\/review/, "Practice Review"],
    [/^\/practice/, "Practice"],
    [/^\/keyword-practice/, "Keyword Practice"],
    [/^\/mock-exams/, "Mock Exams"],
    [/^\/exam-history/, "Exam History"],
    [/^\/exam\/results/, "Exam Results"],
    [/^\/exam/, "Exam"],
    [/^\/insights/, "Insights"],
    [/^\/notifications/, "Notifications"],
    [/^\/settings/, "Settings"],
    [/^\/pricing/, "Pricing"],
    [/^\/billing/, "Billing"],
    [/^\/admin\/announcements/, "Admin · Announcements"],
    [/^\/admin\/auth-events/, "Admin · Auth Events"],
    [/^\/admin\/certifications/, "Admin · Certifications"],
    [/^\/admin\/chapters/, "Admin · Chapters"],
    [/^\/admin\/data/, "Admin · Data Management"],
    [/^\/admin\/exam-sets/, "Admin · Exam Sets"],
    [/^\/admin\/keywords/, "Admin · Keywords"],
    [/^\/admin\/learning-objectives/, "Admin · Learning Objectives"],
    [/^\/admin\/pages/, "Admin · Pages"],
    [/^\/admin\/quality/, "Admin · Quality"],
    [/^\/admin\/questions/, "Admin · Questions"],
    [/^\/admin\/subscriptions/, "Admin · Subscriptions"],
    [/^\/admin\/users/, "Admin · Users"],
    [/^\/admin/, "Admin"],
  ];
  for (const [re, label] of map) {
    if (re.test(p)) return `${label} | ${SITE_SUFFIX}`;
  }
  return `${SITE_SUFFIX} | Certification Preparation Platform`;
}

interface AppShellProps {
  title?: string;
  subtitle?: string;
  children?: ReactNode;
}

const AppShell = ({ title, subtitle, children }: AppShellProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarClosing, setSidebarClosing] = useState(false);
  const [sidebarAnimatingIn, setSidebarAnimatingIn] = useState(false);
  const closingTimer = useRef<ReturnType<typeof setTimeout>>();
  const mainRef = useRef<HTMLElement>(null);

  const openSidebar = useCallback(() => {
    setSidebarOpen(true);
    setSidebarAnimatingIn(false);
    // Force a frame so the initial -translate-x-full is painted before we animate in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setSidebarAnimatingIn(true));
    });
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarClosing(true);
    setSidebarAnimatingIn(false);
    clearTimeout(closingTimer.current);
    closingTimer.current = setTimeout(() => {
      setSidebarOpen(false);
      setSidebarClosing(false);
    }, 320);
  }, []);

  useEffect(() => () => clearTimeout(closingTimer.current), []);
  const { isAdmin, profile, signOut, user } = useAuth();
  // Announce this session on the shared presence channel so admins can see
  // who is currently online.
  usePresenceTracker(user?.id ?? null);
  const safeTopFallback = useSafeAreaTop();
  const { enabled: screenshotMode } = useScreenshotMode();

  const isAdminView = location.pathname.startsWith("/admin");

  const handleToggleView = () => {
    if (isAdminView) {
      navigate("/dashboard");
    } else {
      navigate("/admin/certifications");
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  const initials = profile?.full_name
    ? profile.full_name
        .replace(/\s*\(.*?\)\s*/g, " ") // strip parenthesized labels like "(admin)"
        .trim()
        .split(" ")
        .filter(Boolean)
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "U";

  // Close mobile sidebar on route change
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;
      if (sidebarOpen) closeSidebar();
    }
  }, [location.pathname, sidebarOpen, closeSidebar]);

  // Scroll the page content back to the top whenever the route changes so
  // users don't land mid-page when navigating between sections.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  // Keep the browser tab title in sync with the current authenticated route
  // so it doesn't get stuck on a prior page (e.g. "Sign In").
  useEffect(() => {
    document.title = titleForPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const root = document.getElementById("root");

    // Apply the app-wide 4px scrollbar class to the document element so the
    // mobile document scrollbar (and any uncaught overflow containers) match
    // the thin style used inside AppShell.
    document.documentElement.classList.add("app-scrollbar-thin");

    // Only lock body scroll on desktop where AppShell manages its own internal
    // scroll container. On mobile we rely on native document scrolling, so
    // locking overflow here would make the page non-scrollable.
    const mq = window.matchMedia("(min-width: 1024px)");
    if (!mq.matches) {
      return () => {
        document.documentElement.classList.remove("app-scrollbar-thin");
      };
    }

    const prevHtmlOverflow = document.documentElement.style.getPropertyValue("overflow");
    const prevHtmlOverflowPriority = document.documentElement.style.getPropertyPriority("overflow");
    const prevBodyOverflow = document.body.style.getPropertyValue("overflow");
    const prevBodyOverflowPriority = document.body.style.getPropertyPriority("overflow");
    const prevRootHeight = root?.style.getPropertyValue("height") ?? "";
    const prevRootHeightPriority = root?.style.getPropertyPriority("height") ?? "";

    document.documentElement.style.setProperty("overflow", "hidden", "important");
    document.body.style.setProperty("overflow", "hidden", "important");
    root?.style.setProperty("height", "100%", "important");

    return () => {
      document.documentElement.classList.remove("app-scrollbar-thin");

      if (prevHtmlOverflow) {
        document.documentElement.style.setProperty("overflow", prevHtmlOverflow, prevHtmlOverflowPriority || undefined);
      } else {
        document.documentElement.style.removeProperty("overflow");
      }

      if (prevBodyOverflow) {
        document.body.style.setProperty("overflow", prevBodyOverflow, prevBodyOverflowPriority || undefined);
      } else {
        document.body.style.removeProperty("overflow");
      }

      if (root) {
        if (prevRootHeight) {
          root.style.setProperty("height", prevRootHeight, prevRootHeightPriority || undefined);
        } else {
          root.style.removeProperty("height");
        }
      }
    };
  }, []);

  return (
    <div className="min-h-[100dvh] lg:h-[100dvh] bg-sidebar-background lg:overflow-hidden flex justify-center">
      <DemoModeIndicator />
      <ScreenshotLinkMasker />
      <SampleExamModal />

      <div className={`flex min-h-[100dvh] lg:h-full w-full ${isAdminView ? '' : 'max-w-[1440px]'}`}>
        {/* Desktop sidebar */}
        <div className="hidden lg:block h-full">
          {isAdminView ? <AdminSidebar /> : <UserSidebar />}
        </div>

        {/* Mobile sidebar overlay with swipe-to-close + slide animation */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <button
              type="button"
              aria-label="Close menu"
              className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ease-in-out ${
                sidebarClosing ? "opacity-0" : "opacity-100"
              }`}
              onClick={closeSidebar}
            />
            <div
              className={`relative z-50 h-[100dvh] w-60 transition-transform duration-300 ease-in-out ${
                sidebarAnimatingIn && !sidebarClosing ? "translate-x-0" : "-translate-x-full"
              }`}
              onTouchStart={(e) => {
                const touch = e.touches[0];
                (e.currentTarget as HTMLElement).dataset.touchStartX = String(touch.clientX);
              }}
              onTouchEnd={(e) => {
                const startX = Number((e.currentTarget as HTMLElement).dataset.touchStartX ?? 0);
                const endX = e.changedTouches[0].clientX;
                if (startX - endX > 60) {
                  closeSidebar();
                }
              }}
            >
              {isAdminView ? <AdminSidebar /> : <UserSidebar />}
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col min-w-0 lg:min-h-0 bg-muted lg:overflow-hidden">
          {!screenshotMode && <PaymentTestModeBanner />}
          <PastDueBanner />
          {/* Top Header Bar */}
          <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 lg:px-6" style={{ paddingTop: `calc(env(safe-area-inset-top, 0px) + ${safeTopFallback}px)`, minHeight: `calc(3.5rem + env(safe-area-inset-top, 0px) + ${safeTopFallback}px)` }}>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-muted transition-colors"
              onClick={() => sidebarOpen ? closeSidebar() : openSidebar()}
              aria-label="Toggle menu"
            >
              <Menu className="h-5 w-5 text-foreground" />
            </button>

            <div className="flex items-center gap-2 ml-1">
              {!screenshotMode && <BetaBadge />}
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-3">
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                    <Avatar className="h-8 w-8 cursor-pointer">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/settings")}>
                    <Settings className="h-4 w-4 mr-2" />
                    Account Settings
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="cursor-pointer" onClick={handleToggleView}>
                        <Shield className="h-4 w-4 mr-2" />
                        {isAdminView ? "Switch to Learner" : "Switch to Admin"}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={handleSignOut}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Page Content */}
          <main ref={mainRef} className="flex-1 lg:min-h-0 p-4 lg:p-6 lg:overflow-y-auto lg:overscroll-y-contain bg-muted scrollbar-thin">
            <div className={isAdminView ? '' : 'max-w-[1280px] mx-auto'}>
              {children ? (
                <>
                  {title && (
                    <div className="mb-8">
                      <h1 className="text-2xl font-bold text-foreground mb-1">{title}</h1>
                      {subtitle && (
                        <p className="text-sm text-muted-foreground">{subtitle}</p>
                      )}
                    </div>
                  )}
                  <div className="space-y-8">
                    {children}
                  </div>
                </>
              ) : (
                <Suspense fallback={<PageContentSkeleton />}>
                  <Outlet />
                </Suspense>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppShell;
